import type { AppNavParams } from '@euler/app/Routes';
import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { StackLayout } from '@euler/app/components/StackLayout';
import { ListItemDisclosure } from '@euler/app/components/list/ListItemDisclosure';
import { Typography } from '@euler/app/components/typography';
import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import type { ServiceRecordNavParams } from '@euler/app/flows/record/ServiceRecordFlow';
import type { ReportNavParams } from '@euler/app/flows/report/ReportScreens';
import { reportTabKeyFromScreen } from '@euler/app/flows/report/functions/util';
import type { ProductManifestProps } from '@euler/app/flows/report/types';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useShareReport } from '@euler/functions/sharing/useShareReport';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import {
  CommonTaskStatus,
  SamplingCheckResultType,
  StoreFeature,
} from '@euler/model/enum';
import {
  SamplingCheck,
  TaskProductManifestItem,
} from '@euler/model/task-detail';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { BehaviorSubject } from 'rxjs';

export const ReportList = memo(() => {
  const isMobile = useIsMobileLayout();
  const nav = useAppNavigation();
  const confirm = useConfirm();
  const samplingCheck = useStoreFeature(StoreFeature.SamplingCheck).enabled;

  const { rpx } = useResponsiveContext();
  const { serviceRecord, reportTarget, refresh, storeConfiguration } =
    useServiceRecordContext();

  const navigation =
    useNavigation<StackNavigationProp<AppNavParams & ServiceRecordNavParams>>();

  const { shareReportToCustomer } = useShareReport();
  const [forceRefresh, setForceRefresh] = useState(0);

  const productManifest$ = useCreation(
    () =>
      new BehaviorSubject(
        serviceRecord.productReferrals?.map<TaskProductManifestItem>(x => ({
          id: x.id,
          author: x.author,
          product: x.product,
          qty: x.qty ?? 1,
          subject: x.subject,
          jobRefs: x.jobRefs,
          origin: x.origin!,
          confirmations: x.confirmations,
        })) ?? [],
      ),
    [serviceRecord.productReferrals],
  );

  const productManifestProps = useMemo<ProductManifestProps>(() => {
    return {
      items$: productManifest$,
      onViewDetail: item => {
        nav.push('_productDetail', {
          id: item.product.id,
        });
      },
    };
  }, [nav, productManifest$]);

  const onPress = usePersistFn((type: keyof ReportNavParams) => {
    if (
      type === 'samplingCheckReport' &&
      serviceRecord.samplingCheckStatus !== CommonTaskStatus.Finished
    ) {
      // start sampling check.
      nav.push('Task', {
        taskNo: serviceRecord.taskNo,
        continueTo: '_samplingCheck',
        from: 'service-record',
        onEnd: () => {
          void refresh(true);
          setForceRefresh(x => x + 1);
        },
      });
      return;
    }
    navigation.push(type, {
      target: reportTarget,
      standalone: true,
      ...{
        productManifest: productManifestProps,
      },
      ...(type === 'samplingCheckReport' &&
      storeConfiguration.allowSamplingCheckAfterFinish
        ? {
            secondaryAction: {
              text: '修改报告',
              bordered: !isMobile,
              variant: 'danger',
              style: { marginRight: rpx(10) },
              onPress: async () => {
                const confirmed = await confirm({
                  title: '是否确认修改抽检报告?',
                  confirmButtonText: '确认',
                  cancelButtonText: '取消',
                });
                if (!confirmed) {
                  return;
                }
                nav.replace('Task', {
                  taskNo: serviceRecord.taskNo,
                  from: 'service-record',
                  continueTo: '_samplingCheck',
                  continueParams: { edit: true },
                  onEnd: () => {
                    void refresh(true);
                    setForceRefresh(x => x + 1);
                  },
                });
              },
            },
          }
        : undefined),
      onShare:
        type === 'samplingCheckReport'
          ? undefined
          : () => {
              void shareReportToCustomer({
                target: reportTarget,
                orderNo: reportTarget.orderNo,
                reportNo: reportTarget.reportNo,
                licensePlateNo: reportTarget.licensePlateNo,
                tab: reportTabKeyFromScreen(type),
                success() {
                  void refresh();
                },
              });
            },
    });
  });

  return (
    <TaskDetailSection
      title="服务报告"
      key={forceRefresh}
      noBodyPadding
      headerStyle={{ borderBottomWidth: 0 }}
      css={`
        margin-bottom: ${rpx(15)}px;
      `}
    >
      {serviceRecord.preInspectionStatus === CommonTaskStatus.Finished && (
        <>
          <Separator />
          <Cell
            taskNo={serviceRecord.taskNo}
            title="接车预检"
            type="preInspectionReport"
            shared={serviceRecord.reportShareSummary?.preInspection}
            viewed={serviceRecord.reportViewSummary?.preInspection}
            onPress={onPress}
          />
        </>
      )}
      {serviceRecord.inspectionStatus === CommonTaskStatus.Finished && (
        <>
          <Separator />
          <Cell
            taskNo={serviceRecord.taskNo}
            title="常规检查"
            type="inspectionReport"
            shared={serviceRecord.reportShareSummary?.inspection}
            viewed={serviceRecord.reportViewSummary?.inspection}
            onPress={onPress}
          />
        </>
      )}
      {serviceRecord.constructionStatus === CommonTaskStatus.Finished && (
        <>
          <Separator />
          <Cell
            taskNo={serviceRecord.taskNo}
            title="施工反馈"
            type="constructionReport"
            shared={serviceRecord.reportShareSummary?.construction}
            viewed={serviceRecord.reportViewSummary?.construction}
            onPress={onPress}
          />
        </>
      )}
      {serviceRecord.deliveryCheckStatus === CommonTaskStatus.Finished && (
        <>
          <Separator />
          <Cell
            taskNo={serviceRecord.taskNo}
            title="竣工检验"
            type="deliveryCheckReport"
            shared={serviceRecord.reportShareSummary?.deliveryCheck}
            viewed={serviceRecord.reportViewSummary?.deliveryCheck}
            onPress={onPress}
          />
        </>
      )}
      {(serviceRecord.samplingCheckStatus === CommonTaskStatus.Finished ||
        (samplingCheck &&
          storeConfiguration.allowSamplingCheckAfterFinish)) && (
        <>
          <Separator />
          <Cell
            taskNo={serviceRecord.taskNo}
            title="完工抽检"
            type="samplingCheckReport"
            viewed={null}
            shared={null}
            canDoSamplingCheck={
              storeConfiguration.allowSamplingCheckAfterFinish &&
              serviceRecord.samplingCheckStatus !== CommonTaskStatus.Finished
            }
            onPress={onPress}
            subTitle={
              serviceRecord.samplingCheckStatus === CommonTaskStatus.Finished &&
              serviceRecord.samplingCheckResult != null ? (
                <Typography.Text
                  color={
                    serviceRecord.samplingCheckResult ===
                    SamplingCheckResultType.Passed
                      ? StatusColors.Success
                      : StatusColors.Danger
                  }
                  size={rpx(12)}
                  lineHeight={rpx(21)}
                  medium
                >
                  {serviceRecord.samplingCheckResult ===
                  SamplingCheckResultType.Passed
                    ? '(抽检合格)'
                    : '(抽检不合格)'}
                </Typography.Text>
              ) : undefined
            }
          />
        </>
      )}
      {serviceRecord.preInspectionStatus !== CommonTaskStatus.Finished &&
      serviceRecord.inspectionStatus !== CommonTaskStatus.Finished &&
      serviceRecord.constructionStatus !== CommonTaskStatus.Finished &&
      serviceRecord.deliveryCheckStatus !== CommonTaskStatus.Finished ? (
        <EmptyView
          noFullHeight
          message="无相关服务报告"
          style={{ paddingBottom: rpx(20) }}
          messageStyle={{ marginTop: rpx(0) }}
          iconColor="#ccc"
          iconSize={rpx(32)}
        />
      ) : null}
    </TaskDetailSection>
  );
});

const Cell = memo(
  ({
    type,
    taskNo,
    title,
    subTitle,
    shared,
    viewed,
    style,
    canDoSamplingCheck,
    onPress,
  }: {
    taskNo: string;
    type: keyof ReportNavParams;
    title: string;
    subTitle?: ReactNode;
    viewed?: boolean | null;
    shared?: boolean | null;
    style?: StyleProp<ViewStyle>;
    canDoSamplingCheck?: boolean;
    onPress?: (type: keyof ReportNavParams) => void;
  }) => {
    const { taskService } = useServiceFactory();
    const [loading, setLoading] = useState(false);
    const [samplingCheck, setSamplingCheck] = useState<SamplingCheck>();
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(type);
    }, [onPress, type]);
    useEffect(() => {
      if (!canDoSamplingCheck) {
        return;
      }
      setLoading(true);
      taskService
        .getTaskDetail(taskNo, {
          projection: { samplingCheck: true },
        })
        .then(res => {
          setSamplingCheck(res.response.samplingCheck);
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }, [canDoSamplingCheck, taskNo, taskService]);
    return (
      <TouchableOpacity
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding: ${rpx(10)}px ${rpx(6)}px ${rpx(10)}px ${rpx(15)}px;
        `}
        style={style}
        onPress={handlePress}
      >
        <StackLayout direction="row" alignItems="center" gap={rpx(8)}>
          <Typography.Label regular size={rpx(16)} lineHeight={rpx(21)}>
            {title}
          </Typography.Label>
          {subTitle}
        </StackLayout>
        <HStack
          justifyContent="flex-end"
          css={`
            flex: 1;
          `}
        >
          {shared ? (
            <HStack
              justifyContent="flex-start"
              css={`
                margin-right: ${rpx(8)}px;
              `}
            >
              <Icon
                name="share-arrow"
                size={rpx(12)}
                color={StatusColors.Success}
              />
              <Typography.Label
                color={StatusColors.Success}
                size={rpx(12)}
                lineHeight={rpx(16)}
                css={`
                  margin-left: ${rpx(4)}px;
                `}
              >
                已分享
              </Typography.Label>
            </HStack>
          ) : null}
          {type === 'samplingCheckReport' &&
          canDoSamplingCheck &&
          (loading || samplingCheck != null) ? (
            <Center
              css={`
                background-color: ${loading
                  ? 'transparent'
                  : samplingCheck == null
                  ? StatusColors.Warn
                  : samplingCheck.status === CommonTaskStatus.Finished
                  ? StatusColors.Success
                  : samplingCheck.status === CommonTaskStatus.InProgress
                  ? '#B381B3'
                  : StatusColors.Warn};
                border-radius: ${rpx(3)}px;
                padding: 0 ${rpx(3)}px;
              `}
            >
              {loading ? (
                <ActivityIndicator
                  size="small"
                  style={{ transform: [{ scale: 0.5 }] }}
                />
              ) : (
                <Typography.Label
                  size={rpx(10)}
                  lineHeight={rpx(16)}
                  color={'white'}
                  css={`
                    text-align: right;
                    margin-right: ${rpx(4)}px;
                    margin-left: ${rpx(4)}px;
                  `}
                >
                  {samplingCheck == null
                    ? '未开始, 点击进行抽检'
                    : samplingCheck.status === CommonTaskStatus.Finished
                    ? '已抽检'
                    : samplingCheck.status === CommonTaskStatus.InProgress
                    ? '抽检中'
                    : '未开始, 点击进行抽检'}
                </Typography.Label>
              )}
            </Center>
          ) : (
            <>
              {viewed ? (
                <Icon
                  size={rpx(12)}
                  name="checkmark-circle"
                  color={StatusColors.Success}
                />
              ) : (
                <Icon
                  size={rpx(12)}
                  name={viewed == null ? 'information-solid' : 'clock'}
                  color={viewed == null ? '#E3BC00' : '#ccc'}
                />
              )}
              <Typography.Label
                size={rpx(12)}
                lineHeight={rpx(16)}
                color={
                  viewed
                    ? StatusColors.Success
                    : viewed == null
                    ? '#E3BC00'
                    : '#ccc'
                }
                css={`
                  text-align: right;
                  margin-right: ${rpx(4)}px;
                  margin-left: ${rpx(4)}px;
                `}
              >
                {viewed
                  ? '客户已查看'
                  : viewed === null
                  ? '仅内部查看'
                  : '客户未查看'}
              </Typography.Label>
            </>
          )}
        </HStack>
        <ListItemDisclosure />
      </TouchableOpacity>
    );
  },
);

const Separator = memo(() => {
  return (
    <View
      css={`
        height: ${StyleSheet.hairlineWidth}px;
        background-color: #ddd;
      `}
    />
  );
});
